import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Popconfirm, Space, Table, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, PrinterOutlined, RetweetOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { sciService, type StageCompletionInspection } from '@/services/qc-workflow.service';
import type { ReportMetadata } from './DimensionalInspectionPage';

const { Search } = Input;
const { Title } = Typography;

function openDimensionalInspectionPrint(id: string): void {
  window.location.assign(`/print/qms/dimensional-inspections/${id}`);
}

const DimensionalInspectionRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');

  const { data: inspectionRows = [], isLoading } = useQuery({
    queryKey: ['quality', 'dimensional-inspections'],
    queryFn: async () => {
      const response = await sciService.getAll();
      return (response.data?.data || response.data || []) as StageCompletionInspection[];
    },
  });

  const deleteInspectionMutation = useMutation({
    mutationFn: async (id: string) => {
      await sciService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'dimensional-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      message.success('Dimensional inspection deleted');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete dimensional inspection');
    },
  });

  const filteredInspectionRows = useMemo(() => {
    const dimensionalRows = inspectionRows.filter((row) => {
      const meta = (row.report_metadata || {}) as ReportMetadata;
      return Boolean(
        String(meta.report_no || '').trim() ||
        String(meta.date_of_inspection || '').trim() ||
        String(meta.client || '').trim() ||
        String(meta.drawing_reference || '').trim() ||
        String(meta.part_no_description || '').trim() ||
        String(meta.inspected_by || '').trim()
      );
    });

    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return dimensionalRows;

    return dimensionalRows.filter((row) => {
      const meta = (row.report_metadata || {}) as ReportMetadata;
      const haystack = [
        meta.report_no,
        meta.client,
        meta.drawing_reference,
        meta.part_no_description,
        meta.inspected_by,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [inspectionRows, searchText]);

  const columns: ColumnsType<StageCompletionInspection> = useMemo(
    () => [
      {
        title: 'Report No.',
        key: 'report_no',
        width: 160,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).report_no || '—',
      },
      {
        title: 'Date of Inspection',
        key: 'date_of_inspection',
        width: 160,
        render: (_, row) => {
          const value = ((row.report_metadata || {}) as ReportMetadata).date_of_inspection;
          return value ? dayjs(value).format('DD MMM YYYY').toUpperCase() : '—';
        },
      },
      {
        title: 'Client',
        key: 'client',
        width: 300,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).client || '—',
      },
      {
        title: 'Drawing Ref.',
        key: 'drawing_reference',
        width: 180,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).drawing_reference || '—',
      },
      {
        title: 'Part No./Description',
        key: 'part_no_description',
        width: 220,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).part_no_description || '—',
      },
      {
        title: 'Inspected By',
        key: 'inspected_by',
        width: 190,
        render: (_, row) => ((row.report_metadata || {}) as ReportMetadata).inspected_by || '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 210,
        render: (_, row) => (
          <Space size="small" wrap={false}>
            <Tooltip title="Edit">
              <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/qms/dimensional-inspection/${row.id}/edit`)} />
            </Tooltip>
            <Tooltip title="Clone">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() =>
                  navigate('/qms/dimensional-inspection/new', {
                    state: { sourceInspectionId: row.id, prefillMode: 'clone' },
                  })
                }
              />
            </Tooltip>
            <Tooltip title="Revision">
              <Button
                size="small"
                icon={<RetweetOutlined />}
                onClick={() =>
                  navigate('/qms/dimensional-inspection/new', {
                    state: { sourceInspectionId: row.id, prefillMode: 'revision' },
                  })
                }
              />
            </Tooltip>
            <Tooltip title="Print">
              <Button size="small" icon={<PrinterOutlined />} onClick={() => openDimensionalInspectionPrint(row.id)} />
            </Tooltip>
            <Popconfirm title="Delete dimensional inspection?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => deleteInspectionMutation.mutate(row.id)}>
              <Tooltip title="Delete">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteInspectionMutation.isPending && deleteInspectionMutation.variables === row.id}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteInspectionMutation, navigate]
  );

  return (
    <div className="dimensional-inspection-page" style={{ padding: '16px 12px', maxWidth: 1600, margin: '0 auto' }}>
      <Title level={4} style={{ marginBottom: 16 }}>Dimensional Inspection</Title>

      <Card style={{ marginBottom: 16 }}>
        <Space
          wrap
          style={{
            marginBottom: 16,
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Search
            allowClear
            placeholder="Search report no, client, drawing ref, part, inspected by..."
            style={{ width: 360 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={setSearchText}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qms/dimensional-inspection/new')}>
            Add Dimensional Inspection
          </Button>
        </Space>

        <Table<StageCompletionInspection>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={filteredInspectionRows}
          scroll={{ x: 'max-content' }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default DimensionalInspectionRegisterPage;
